import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Receipt, Truck, CreditCard, CurrencyInr, Package, CheckCircle, XCircle, CircleNotch, Star, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import StarRating from "@/components/StarRating";

const STATUS_KEY = {
  pending: "orders.status.pending",
  paid: "orders.status.paid",
  cod_pending: "orders.status.cod_pending",
  cod_delivered: "orders.status.cod_delivered",
  cancelled: "orders.status.cancelled",
  expired: "orders.status.cancelled",
  failed: "orders.status.cancelled",
};

const STATUS_COLOR = {
  pending: "#D49A36",
  paid: "#2C4A3B",
  cod_pending: "#D49A36",
  cod_delivered: "#2C4A3B",
  cancelled: "#8A2E2E",
  expired: "#8A2E2E",
  failed: "#8A2E2E",
};

function providerIcon(p) {
  if (p === "cod") return Truck;
  if (p === "razorpay") return CurrencyInr;
  return CreditCard;
}

export default function Orders() {
  const { user } = useAuth();
  const { t } = useLang();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("buyer");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const [reviewed, setReviewed] = useState({}); // order_id -> true when review submitted

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/orders/mine"); setOrders(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load orders"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user && user !== false) load(); }, [user]);

  const update = async (o, action) => {
    setBusy(o.id);
    try {
      await api.post("/orders/cod/update", { order_id: o.id, action });
      toast.success(action === "delivered" ? "Marked delivered" : "Order cancelled");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed"); }
    finally { setBusy(null); }
  };

  if (!user || user === false) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <Receipt size={40} className="mx-auto text-[#2C4A3B]" />
        <h1 className="font-heading text-4xl mt-4">{t("orders.title")}</h1>
        <Link to="/login" className="samaaj-btn-primary mt-4 inline-block">{t("nav.login")}</Link>
      </div>
    );
  }

  const filtered = orders.filter((o) => o.role === tab);

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D49A36]">{t("nav.marketplace")}</div>
      <h1 className="font-heading text-5xl mt-2 tracking-tight">{t("orders.title")}</h1>
      <p className="text-[#5C564D] mt-2">{t("orders.sub")}</p>

      <div className="mt-6 inline-flex p-1 rounded-full bg-[#F2EBE5] border border-[#E5D9CC]" data-testid="orders-tabs">
        {["buyer", "seller"].map((v) => (
          <button
            key={v}
            data-testid={`orders-tab-${v}`}
            onClick={() => setTab(v)}
            className="px-4 py-1.5 rounded-full text-sm font-semibold"
            style={{
              background: tab === v ? "#D95A41" : "transparent",
              color: tab === v ? "#F9F6F0" : "#5C564D",
              transition: "background-color 180ms ease-out, color 180ms ease-out",
            }}
          >
            {t(v === "buyer" ? "orders.tab.buyer" : "orders.tab.seller")}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="text-[#5C564D]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="samaaj-card p-10 text-center">
            <Package size={32} className="mx-auto text-[#5C564D]" />
            <div className="font-heading text-2xl mt-3">{t("orders.none")}</div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="orders-list">
            {filtered.map((o) => {
              const ps = o.payment_status || "pending";
              const Icon = providerIcon(o.provider);
              const isCOD = o.provider === "cod";
              const canDeliver = isCOD && tab === "seller" && ps === "cod_pending";
              const canCancel = isCOD && ps === "cod_pending";
              return (
                <div key={o.id} className="samaaj-card p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid={`order-${o.id}`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "#F2EBE5" }}>
                    <Icon size={18} className="text-[#2C4A3B]" weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg text-[#1C1B1A]">{o.listing_title || "Item"}</div>
                    <div className="text-xs text-[#5C564D] mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="uppercase tracking-wider font-bold" style={{ color: STATUS_COLOR[ps] || "#5C564D" }}>
                        {(ps === "paid" || ps === "cod_delivered") && <CheckCircle size={12} weight="fill" className="inline mr-1" />}
                        {["cancelled", "expired", "failed"].includes(ps) && <XCircle size={12} weight="fill" className="inline mr-1" />}
                        {t(STATUS_KEY[ps] || "orders.status.pending")}
                      </span>
                      <span>·</span>
                      <span>{o.provider === "cod" ? "Cash on Delivery" : o.provider === "coupon" ? "Coupon (legacy)" : o.provider === "razorpay" ? "Razorpay (historical)" : "Stripe (historical)"}</span>
                      <span>·</span>
                      <span>{o.currency?.toUpperCase() || "USD"} {(+o.amount || 0).toFixed(2)}</span>
                    </div>
                    {isCOD && o.delivery_address && tab === "seller" && (
                      <div className="text-xs text-[#5C564D] mt-1">📍 {o.delivery_address} · {o.buyer_phone}</div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    {canDeliver && (
                      <button data-testid={`btn-deliver-${o.id}`} disabled={busy === o.id} onClick={() => update(o, "delivered")} className="samaaj-btn-primary text-xs">
                        {busy === o.id ? <CircleNotch className="animate-spin" size={12} /> : t("orders.mark_delivered")}
                      </button>
                    )}
                    {canCancel && (
                      <button data-testid={`btn-cancel-${o.id}`} disabled={busy === o.id} onClick={() => update(o, "cancel")} className="samaaj-btn-outline text-xs">
                        {t("orders.cancel")}
                      </button>
                    )}
                    {tab === "buyer" && (ps === "paid" || ps === "cod_delivered") && !reviewed[o.id] && (
                      <button data-testid={`btn-review-${o.id}`} onClick={() => setReviewFor(o)} className="samaaj-btn-primary text-xs flex items-center gap-1">
                        <Star size={12} weight="fill" /> Rate seller
                      </button>
                    )}
                    {tab === "buyer" && reviewed[o.id] && (
                      <span className="text-xs text-[#2C4A3B] font-semibold flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Reviewed</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {reviewFor && (
        <ReviewModal
          order={reviewFor}
          onClose={() => setReviewFor(null)}
          onDone={() => { setReviewed((prev) => ({ ...prev, [reviewFor.id]: true })); setReviewFor(null); }}
        />
      )}
    </div>
  );
}

function ReviewModal({ order, onClose, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) { toast.error("Please pick 1-5 stars"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", { order_id: order.id, rating, comment });
      toast.success("Thanks for the review!");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Review failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4">
      <div className="samaaj-card max-w-md w-full p-6 bg-[#F9F6F0]" data-testid="review-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D49A36]">Rate this seller</div>
            <div className="font-heading text-2xl mt-1">{order.listing_title}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div className="flex flex-col items-center gap-2 py-3">
            <StarRating value={rating} onChange={setRating} size={36} />
            <div className="text-sm text-[#5C564D]">
              {rating === 5 && "Loved it"}
              {rating === 4 && "Really good"}
              {rating === 3 && "It was fine"}
              {rating === 2 && "Not great"}
              {rating === 1 && "Poor experience"}
            </div>
          </div>
          <textarea
            data-testid="review-comment"
            rows={3}
            placeholder="Optional — what stood out?"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-[#E5D9CC]"
          />
          <button data-testid="review-submit" disabled={busy} className="samaaj-btn-primary w-full">
            {busy ? "Sending…" : "Post review"}
          </button>
        </form>
      </div>
    </div>
  );
}
